// Briefing-relative time helpers. Shared by the producer web app and the
// PROduce Producer app (moved here from apps/web/src/lib in WP9), so both
// read a wall-clock assignment in the festival's zone the same way.
//
// Assignments and lineup rows store wall-clock time as DATE + TIME
// (no TZ) — they're interpreted in the festival's local zone
// (briefings.timezone, default Europe/Amsterdam). Earlier code used
// `new Date(\`${date}T${time}\`)`, which JS parses in the *system's*
// local TZ — so a producer reviewing analytics from outside NL saw
// every assignment shifted, and presence comparisons silently broke.
//
// Use these helpers wherever you need to compare assignment HH:MM to
// a real timestamp (presence_logs, crew_locations).

const DEFAULT_TZ = "Europe/Amsterdam";

/** The zone's UTC offset (ms) at a given instant, via Intl. */
function offsetMsAt(instant: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(instant)) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  const hour = parts.hour === "24" ? "00" : parts.hour;
  const tzShows = `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}Z`;
  return Date.parse(tzShows) - instant.getTime();
}

/**
 * Convert a wall-clock (date + time, no TZ) to the absolute UTC
 * instant it represents in `tz`.
 *
 * Implementation: parse the string as if it were UTC, read the zone's
 * offset at that probe instant, subtract — then read the offset AGAIN
 * at the candidate and correct once more.
 *
 * The second read is not optional. With one read, the offset comes from
 * an instant that is `offset` hours away from the true one, so any DST
 * transition inside that gap is read from the wrong side of the clock
 * change. The broken window is as wide as the zone's offset: 1-2 late-
 * night hours in Amsterdam, but a 10-16 hour stretch of festival evening
 * in Sydney or Auckland, twice a year. This was latent for as long as
 * every briefing fell back to Amsterdam; selecting briefings.timezone
 * made the wide-offset zones reachable, and the review caught it by
 * running the algorithm against real transition nights.
 *
 * A wall-clock that does not exist (inside the spring-forward jump)
 * resolves to one consistent side of the gap — good enough for shift
 * bounds; nobody schedules to a time the clock skips.
 */
export function wallClockToUtc(dateStr: string, timeStr: string, tz: string = DEFAULT_TZ): Date {
  const time = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const intendedAsUtc = new Date(`${dateStr}T${time}Z`);

  const firstOffset = offsetMsAt(intendedAsUtc, tz);
  const candidate = new Date(intendedAsUtc.getTime() - firstOffset);

  const secondOffset = offsetMsAt(candidate, tz);
  if (secondOffset === firstOffset) return candidate;
  return new Date(intendedAsUtc.getTime() - secondOffset);
}

/**
 * Format a UTC Date as wall-clock in `tz`. Supports a small subset
 * of date-fns-style tokens: yyyy, MM, dd, HH, mm.
 */
export function formatInTz(date: Date, tz: string, pattern: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return pattern
    .replace(/yyyy/g, parts.year)
    .replace(/MM/g, parts.month)
    .replace(/dd/g, parts.day)
    .replace(/HH/g, hour)
    .replace(/mm/g, parts.minute)
    .replace(/ss/g, parts.second);
}

export const BRIEFING_TZ_DEFAULT = DEFAULT_TZ;
